export const PERMISSION_LEVELS = ["view", "edit", "manage", "full"];

export const MODULES = [
  { module: "dashboard", label: "Dashboard", paths: ["/dashboard"] },
  { module: "users", label: "Users", paths: ["/users", "/usercontactedlist", "/redemptions"] },
  { module: "reports", label: "Reports", paths: ["/reported-users"] },
  { module: "categories", label: "Categories", paths: ["/categorylist"] },
  { module: "posters", label: "Posters", paths: ["/posterlist", "/create-poster"] },
  { module: "trending-posters", label: "Trending Posters", paths: ["/create-trending-category", "/create-trending-posters", "/trending-posters"] },
  { module: "reels", label: "Reels", paths: ["/manage-reel-categories", "/reels"] },
  { module: "audio", label: "Audio", paths: ["/audio"] },
  { module: "logos", label: "Logos", paths: ["/create-logocategory", "/create-logo", "/logolist"] },
  { module: "business-cards", label: "Business Cards", paths: ["/business", "/businesscardlist"] },
  { module: "stickers", label: "Stickers", paths: ["/stickers-category", "/create-sticker"] },
  { module: "banners", label: "Banners", paths: ["/banner"] },
  { module: "plans", label: "Subscription Plans", paths: ["/create-plan", "/planlist", "/userplanlist", "/paymentlist"] },
  { module: "products", label: "Products", paths: ["/add-product", "/productlist"] },
  { module: "orders", label: "Orders", paths: ["/allorders", "/pendingorders", "/orderlist"] },
  { module: "wallet", label: "User Wallet", paths: ["/userwallet"] },
  { module: "amount", label: "Amount Config", paths: ["/amount-config"] },
  { module: "settings", label: "Settings", paths: ["/create-privacy", "/get-policy", "/aboutus", "/getaboutus", "/contactus", "/getcontactus", "/profile"] },
];

const normalize = (value) => String(value || "").toLowerCase().replace(/^\//, "");
const absolutePath = (value) => {
  const path = String(value || "").trim();
  return path ? (path.startsWith("/") ? path : `/${path}`) : null;
};

export const isStaffSession = () => Boolean(localStorage.getItem("staffId"));

export const readStaffPermissions = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("staffPermissions") || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

const levelAtLeast = (actual, required) => {
  const actualIndex = PERMISSION_LEVELS.indexOf(actual);
  const requiredIndex = PERMISSION_LEVELS.indexOf(required);
  return actualIndex >= 0 && requiredIndex >= 0 && actualIndex >= requiredIndex;
};

const recordsForModule = (permissions, module) => permissions.filter(
  (permission) => normalize(permission.module) === normalize(module),
);

const levelForRecord = (record, path) => {
  if (Array.isArray(record.permissions)) {
    if (record.path && normalize(record.path) !== normalize(path)) return null;
    if (record.permissions.includes("full")) return "full";
    return PERMISSION_LEVELS.slice(0, 3).reverse().find((level) => record.permissions.includes(level)) || null;
  }
  const paths = Array.isArray(record.paths) ? record.paths : [];
  const pathRecord = paths.find((item) => normalize(item.path) === normalize(path));
  return pathRecord?.accessLevel || null;
};

export const hasPermission = (module, path, requiredLevel = "view") => {
  if (!isStaffSession()) return true;
  const permissions = readStaffPermissions();
  return recordsForModule(permissions, module).some((record) => {
    const level = levelForRecord(record, path);
    return Boolean(level) && levelAtLeast(level, requiredLevel);
  });
};

export const canAccessPath = (path) => {
  if (!isStaffSession()) return true;
  if (path === "/staff" || path === "/staff/create") return false;
  const module = MODULES.find((item) => item.paths.some((itemPath) => path === itemPath || path.startsWith(`${itemPath}/`)));
  if (module) return hasPermission(module.module, path, "view");
  return readStaffPermissions().some((permission) => {
    if (permission.path === path) return hasPermission(permission.module, path, "view");
    const paths = Array.isArray(permission.paths) ? permission.paths : [];
    return paths.some((item) => item.path === path && hasPermission(permission.module, path, "view"));
  });
};

export const getFirstAccessiblePath = () => {
  const directPermission = readStaffPermissions().find((item) => item.path && hasPermission(item.module, item.path, "view"));
  if (directPermission?.path) return absolutePath(directPermission.path);
  const permission = readStaffPermissions().find((item) => {
    const paths = Array.isArray(item.paths) ? item.paths : [];
    return paths.some((path) => path.path && hasPermission(item.module, path.path, "view"));
  });
  const path = permission?.paths?.find((item) => item.path && hasPermission(permission.module, item.path, "view"));
  return absolutePath(path?.path);
};

export const normalizePermissions = (permissions) => (Array.isArray(permissions) ? permissions : [])
  .filter((permission) => permission?.module && permission?.path)
  .map((permission) => {
    const selected = permission.permissions || [permission.accessLevel || "view"];
    const highest = selected.includes("full") ? "full" : PERMISSION_LEVELS.slice(0, 3).reverse().find((level) => selected.includes(level)) || "view";
    const highestIndex = PERMISSION_LEVELS.indexOf(highest);
    return {
      module: normalize(permission.module),
      path: permission.path,
      permissions: PERMISSION_LEVELS.slice(0, highestIndex + 1),
    };
  });
